import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  styled,
  tableCellClasses,
  Menu,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Autorenew as AutorenewIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePosition,
  duplicatePosition,
  fetchPositions,
  fetchPositionsByDepartment,
} from "src/features/positionsSlice";
import { fetchUsers, User } from "src/features/administrationSlice";
import { fetchDepartments, Department } from "src/features/departmentsSlice";
import { AppDispatch, RootState } from "src/store";
import { useLocation, useNavigate } from "react-router-dom";
import JobCreatePage from "src/components/position/JobCreatePage";
import LoadingSpinner from "src/components/shared/LoadingSpinner";
import { useSnackbar } from "src/components/SnackbarProvider";
import { useTranslation } from "react-i18next";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.grey[100],
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const PositionsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { openSnackbar } = useSnackbar();
  const departmentIdParam = new URLSearchParams(location.search).get("departmentId");

  const { positions, loading } = useSelector(
    (state: RootState) => state.positions
  );
  const { users } = useSelector((state: RootState) => state.admin);
  const { token, user } = useSelector((state: RootState) => state.auth);
  const { departments, loading: departmentsLoading } = useSelector((state: RootState) => state.departments);
  const companyId =
    user?.accountType === "company"
      ? (user.companyId || user.id || user.userId || "")
      : "";

  const [open, setOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(
    null
  );
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [bulkActionMode, setBulkActionMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSingleOpen, setDialogSingleOpen] = useState(false);
  const [ownershipDialogOpen, setOwnershipDialogOpen] = useState(false);
  const [confirmModalText, setConfirmModalText] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Fetch departments on page load
  useEffect(() => {
    if (!token || !companyId) return;
    dispatch(fetchDepartments());
  }, [dispatch, token, companyId]);

  // Fetch positions based on department filter
  useEffect(() => {
    if (!token || !companyId) return;
    if (departmentIdParam) {
      dispatch(fetchPositionsByDepartment(departmentIdParam));
    } else {
      dispatch(fetchPositions());
    }
  }, [dispatch, departmentIdParam, token, companyId]);

  // Sync selected department from query param
  useEffect(() => {
    if (!departmentIdParam) {
      setSelectedDepartment(null);
      return;
    }
    const match = departments.find((d) => d.id === departmentIdParam) || null;
    setSelectedDepartment(match);
  }, [departmentIdParam, departments]);

  // Debug: Log departments when they change
  useEffect(() => {
    console.log('📋 Departments loaded:', departments);
    console.log('📋 Departments count:', departments?.length || 0);
  }, [departments]);

  // Debug: Log positions when they change
  useEffect(() => {
    console.log('💼 Positions loaded:', positions);
    console.log('💼 Positions count:', positions?.length || 0);
  }, [positions]);

  if (loading || departmentsLoading) return <LoadingSpinner />;

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    positionId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedPositionId(positionId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPositionId(null);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPositions(
      event.target.checked ? positions.map((position) => position.id) : []
    );
  };

  const handleCheckboxClick = (id: string) => {
    setSelectedPositions((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((positionId) => positionId !== id)
        : [...prevSelected, id]
    );
  };

  type ActionType = "DELETE" | "ARCHIVE" | "CHANGE";

  const handleBulkAction = (actionType: ActionType) => {
    setConfirmModalText(actionType);
    if (actionType === "CHANGE") {
      setDialogOpen(true);
      setOwnershipDialogOpen(false);
      dispatch(fetchUsers());
    } else {
      setDialogOpen(true);
    }
    setConfirmText("");
  };

  type SingleActionType = "DELETE" | "ARCHIVE";
  const handleSingleAction = async (actionType: SingleActionType) => {
    if (actionType === "DELETE" && selectedPositionId) {
      // Find the position to get its department ID
      const positionToDelete = positions.find((pos) => pos.id === selectedPositionId);
      if (positionToDelete) {
        const departmentId = (positionToDelete as any).departmentId || positionToDelete.department;
        await dispatch(deletePosition({
          departmentId: departmentId,
          positionId: selectedPositionId
        }));
        openSnackbar(t("positions.messages.deleted"), "success");
      }
      setSelectedPositionId(null);
    } else if (actionType === "ARCHIVE") {
      console.log("Position archived: ", selectedPositionId);
    }
    setDialogSingleOpen(false);
  };

  const handleConfirmAction = async (actionType: ActionType) => {
    if (confirmText === actionType) {
      if (actionType === "DELETE") {
        await Promise.all(
          selectedPositions.map(async (positionId) => {
            // Find each position to get its department ID
            const positionToDelete = positions.find((pos) => pos.id === positionId);
            if (positionToDelete) {
              const departmentId = (positionToDelete as any).departmentId || positionToDelete.department;
              await dispatch(deletePosition({
                departmentId: departmentId,
                positionId: positionId
              }));
              openSnackbar(t("positions.messages.deleted"), "success");
            }
          })
        );
        setSelectedPositions([]);
      } else if (actionType === "ARCHIVE") {
        console.log("Selected positions to archive: ", selectedPositions);
      } else if (actionType === "CHANGE") {
        handleOwnershipChange();
      }
    }
    setConfirmText("");
    setBulkActionMode(false);
    setDialogOpen(false);
  };

  const handleOwnershipChange = () => {
    if (selectedUser) {
      console.log("Ownership changed to: ", selectedUser.name);
      console.log("For positions: ", selectedPositions);
    }
    setOwnershipDialogOpen(false);
  };

  const handleDuplicatePosition = async (positionId: string | null) => {
    console.log('🐛 DEBUG - Duplicate clicked:', { positionId })

    if (!positionId) {
      console.log('🐛 DEBUG - Missing positionId')
      return;
    }

    const positionToDuplicate = positions.find((pos) => pos.id === positionId);
    console.log('🐛 DEBUG - Position to duplicate:', positionToDuplicate)

    if (positionToDuplicate) {
      // Use the position's own department ID (raw backend data has departmentId field)
      const departmentId = (positionToDuplicate as any).departmentId || positionToDuplicate.department;
      console.log('🐛 DEBUG - Using departmentId:', departmentId);

      if (!departmentId) {
        console.error('🐛 DEBUG - No department ID found on position:', positionToDuplicate);
        openSnackbar(t("positions.messages.missingDepartment"), "error");
        handleMenuClose();
        return;
      }

      const result = await dispatch(duplicatePosition({
        position: positionToDuplicate,
        departmentId: departmentId
      }));

      console.log('🐛 DEBUG - Duplicate result:', result)

      if (duplicatePosition.fulfilled.match(result)) {
        const newPosition = result.payload;
        openSnackbar(t("positions.messages.duplicated"), "success");
        // Navigate directly to edit page for the new position
        navigate(`/positions/${newPosition.id}`);
      } else if (duplicatePosition.rejected.match(result)) {
        openSnackbar(result.payload as string || t("positions.messages.duplicateFailed"), "error");
      }
    }
    handleMenuClose();
  };

  return (
    <Box
      sx={{
        p: 3,
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          mb: 2,
        }}
      >
        <Typography variant="h5" ml={3}>
          {t("positions.title")}
        </Typography>
        <Box>
          {bulkActionMode ? (
            <Box sx={{ display: "flex" }}>
              <Tooltip title={t("positions.deleteSelected")}>
                <IconButton onClick={() => handleBulkAction("DELETE")}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("positions.archiveSelected")}>
                <IconButton onClick={() => handleBulkAction("ARCHIVE")}>
                  <ArchiveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("positions.changeOwnership")}>
                <IconButton onClick={() => setOwnershipDialogOpen(true)}>
                  <AutorenewIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                onClick={() => {
                  setBulkActionMode(false);
                  setSelectedPositionId("");
                  setSelectedPositions([]);
                }}
                sx={{ ml: 1 }}
              >
                {t("common.cancel")}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpen(true)}
              >
                {t("positions.create")}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setBulkActionMode(true)}
                sx={{ ml: 1 }}
              >
                {t("positions.bulkActions")}
              </Button>
              <IconButton
                onClick={() => dispatch(fetchPositions())}
                sx={{ ml: 1 }}
              >
                <RefreshIcon />
              </IconButton>
              <IconButton sx={{ ml: 1 }}>
                <FilterListIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ width: "100%", mb: 2 }}>
        <Autocomplete
          options={departments}
          getOptionLabel={(option) => option.name || ""}
          value={selectedDepartment}
          onChange={(event, value) => {
            setSelectedDepartment(value);
            if (value?.id) {
              navigate(`/positions?departmentId=${value.id}`);
            } else {
              navigate("/positions");
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("positions.selectDepartment")}
              placeholder={t("positions.selectDepartmentHint")}
              variant="outlined"
              fullWidth
            />
          )}
          noOptionsText={t("positions.noDepartments")}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {bulkActionMode && (
                <StyledTableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selectedPositions.length > 0 &&
                      selectedPositions.length < positions.length
                    }
                    checked={
                      positions.length > 0 &&
                      selectedPositions.length === positions.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </StyledTableCell>
              )}
              <StyledTableCell>{t("positions.table.jobTitle")}</StyledTableCell>
              <StyledTableCell>{t("positions.table.department")}</StyledTableCell>
              <StyledTableCell>{t("positions.table.unit")}</StyledTableCell>
              <StyledTableCell>{t("positions.table.team")}</StyledTableCell>
              <StyledTableCell>{t("positions.table.seniorityLevel")}</StyledTableCell>
              <StyledTableCell>{t("positions.table.workType")}</StyledTableCell>
              <StyledTableCell>{t("positions.table.employmentType")}</StyledTableCell>
              <StyledTableCell>{t("positions.table.salaryRange")}</StyledTableCell>
              <StyledTableCell>{t("positions.table.currency")}</StyledTableCell>
              <StyledTableCell>{t("positions.table.skills")}</StyledTableCell>
              <StyledTableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.length > 0 ? (
              positions.map((position) => {
                const isItemSelected = selectedPositions.includes(position.id);
                return (
                  <StyledTableRow
                    key={position.id}
                    selected={isItemSelected}
                    role="checkbox"
                    aria-checked={isItemSelected}
                  >
                    {bulkActionMode && (
                      <StyledTableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onChange={() => handleCheckboxClick(position.id)}
                        />
                      </StyledTableCell>
                    )}
                    <StyledTableCell>{position.jobTitle}</StyledTableCell>
                    <StyledTableCell>
                      {departments.find((d) => d.id === ((position as any).departmentId || position.department))?.name || (position as any).departmentId || position.department}
                    </StyledTableCell>
                    <StyledTableCell>{position.unit}</StyledTableCell>
                    <StyledTableCell>{position.team}</StyledTableCell>
                    <StyledTableCell>{position.seniorityLevel}</StyledTableCell>
                    <StyledTableCell>{position.workType}</StyledTableCell>
                    <StyledTableCell>{position.employmentType}</StyledTableCell>
                    <StyledTableCell>
                      {position.minSalary} - {position.maxSalary}
                    </StyledTableCell>
                    <StyledTableCell>{position.currency}</StyledTableCell>
                    <StyledTableCell>
                      {(position.skills || []).map((skill) => {
                        const skillName = typeof skill === 'string' ? skill : skill.name
                        return (
                          <Chip key={skillName} label={skillName} sx={{ m: 0.5 }} />
                        )
                      })}
                    </StyledTableCell>
                    <StyledTableCell>
                      <IconButton
                        onClick={(event) => handleMenuClick(event, position.id)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(
                          anchorEl && selectedPositionId === position.id
                        )}
                        onClose={handleMenuClose}
                      >
                        <MenuItem
                          onClick={() => {
                            navigate(`/positions/${position.id}`);
                            handleMenuClose();
                          }}
                        >
                          {t("positions.actions.edit")}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            handleDuplicatePosition(selectedPositionId);
                          }}
                        >
                          {t("positions.actions.duplicate")}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setConfirmModalText("DELETE");
                            setDialogSingleOpen(true);
                            handleMenuClose();
                            setSelectedPositionId(position.id);
                          }}
                        >
                          {t("positions.actions.delete")}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setConfirmModalText("ARCHIVE");
                            setDialogSingleOpen(true);
                            handleMenuClose();
                            setSelectedPositionId(position.id);
                            setSelectedPositions([]);
                          }}
                        >
                          {t("positions.actions.archive")}
                        </MenuItem>
                      </Menu>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={bulkActionMode ? 13 : 12} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    {t("positions.noPositions")}
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {open && (
        <JobCreatePage
          handleClose={setOpen}
          open={open}
          departmentId={selectedDepartment?.id}
        />
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="confirm-dialog"
      >
        <DialogTitle id="confirm-dialog">{t("positions.confirmDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("positions.confirmDialog.message", { action: confirmModalText })}
          </Typography>
          <TextField
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setConfirmText("");
            }}
            color="primary"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => handleConfirmAction(confirmText as ActionType)}
            color="primary"
            variant="contained"
            disabled={
              confirmText !== "DELETE" &&
              confirmText !== "ARCHIVE" &&
              confirmText !== "CHANGE"
            }
          >
            {t("common.continue")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={ownershipDialogOpen}
        onClose={() => setOwnershipDialogOpen(false)}
        aria-labelledby="ownership-dialog"
      >
        <DialogTitle id="ownership-dialog">{t("positions.changeOwnershipDialog.title")}</DialogTitle>
        <DialogContent sx={{ width: 400 }}>
          <Autocomplete
            options={users.map((user) => ({ title: user.name, ...user })) ?? []}
            onChange={(event, value) => setSelectedUser(value)}
            getOptionLabel={(option) => option.title || ""}
            renderInput={(params) => (
              <TextField {...params} label={t("positions.changeOwnershipDialog.newOwner")} sx={{ mt: 2 }} />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOwnershipDialogOpen(false);
              setConfirmModalText("");
              setSelectedPositionId(null);
            }}
            color="primary"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => handleBulkAction("CHANGE")}
            color="primary"
            variant="contained"
            disabled={!selectedUser}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogSingleOpen}
        onClose={() => setDialogSingleOpen(false)}
        aria-labelledby="confirm-dialog"
      >
        <DialogTitle id="confirm-dialog">{t("positions.confirmDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("positions.confirmDialog.deleteMessage", { action: confirmModalText.toLowerCase() })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogSingleOpen(false)} color="primary">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() =>
              handleSingleAction(confirmModalText as SingleActionType)
            }
            color="primary"
            variant="contained"
          >
            {t("positions.confirmDialog.confirmLabel", { action: confirmModalText })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PositionsPage;
